import {
    QueueEntry, IncomingSave, coalesceReportSave, normalizeQueueEntry, erzeugeSchreibbuendel,
    type UngesicherterStand,
} from './offlineQueueLogic';
import { registerBauphaseFlusher } from './bauphaseFlush';

// Dünner IndexedDB-Adapter der Offline-Sync-Queue. Ein Snapshot pro
// Benutzer+Projekt (Key [userId, projectId]); die Zustandslogik liegt
// vollständig in offlineQueueLogic.ts. Schreibvorgänge gelten erst mit
// abgeschlossener Transaktion als gesichert — die UI darf „Lokal gespeichert"
// erst nach dem aufgelösten Promise anzeigen.
//
// JEDER Lesepfad hier läuft durch normalizeQueueEntry: der Speicher überlebt
// App-Versionen, und ein Eintrag in einem abgeschafften Zustand (früher
// 'conflict') wäre sonst für immer unflushbar.
//
// Seit dem 30.09.2026 schreibt das Tippen GEBÜNDELT (`putLatestGebuendelt`,
// Regel und Begründung bei `erzeugeSchreibbuendel`). Damit das niemand
// merkt, der die Warteschlange liest, läuft JEDER Zugriff hier — lesen,
// ändern, löschen, direkt schreiben — durch DIESELBE Kette wie das Bündel und
// stellt sich im Augenblick des Aufrufs an (`nachDemWartenden`, `sofort`).
// Jeder Zugriff ist eine GRENZE: alles vorher Eingetippte geht zuerst
// hinaus, alles danach Eingetippte erst nach ihm (eigene Generation), wer liest,
// sieht den jüngsten Stand, und kein wartender älterer Stand kann
// nachträglich über eine Änderung fallen, die danach kam (etwa den Abschluss
// eines Hochladens, der sonst eine nie hochgeladene Eingabe löschte). Die
// Barriere sitzt hier und nicht bei den Aufrufern, weil es mehr Leser gibt
// als den Hook: App.tsx beim Öffnen und auf dem Rückweg, der Leerlauf-
// Rückzug, der Ausgangskorb des Journals (serviceJournal.ts).
//
// Scheitert ein Schreiben (Speicher voll, abgebrochene Transaktion), bleibt
// der Stand UNGESICHERT im Bündel (siehe dort). Was das für die Zugriffe
// hier heisst, steht an `mitUngesichertem` und `mutateQueueEntry`.

const DB_NAME = 'osia_offline_v1';
const STORE = 'report_saves';

let dbPromise: Promise<IDBDatabase> | null = null;

const openDb = (): Promise<IDBDatabase> => {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, 1);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(STORE)) {
                const store = db.createObjectStore(STORE, { keyPath: ['userId', 'projectId'] });
                store.createIndex('userId', 'userId');
                store.createIndex('state', 'state');
                store.createIndex('nextRetryAt', 'nextRetryAt');
                store.createIndex('updatedAt', 'updatedAt');
            }
        };
        request.onsuccess = () => {
            request.result.onclose = () => { dbPromise = null; };
            resolve(request.result);
        };
        request.onerror = () => { dbPromise = null; reject(request.error); };
        request.onblocked = () => { /* andere Tabs blockieren nur Upgrades */ };
    });
    return dbPromise;
};

const tx = async <T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T> | void): Promise<T> => {
    const db = await openDb();
    return new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const store = transaction.objectStore(STORE);
        let result: T;
        const request = run(store);
        if (request) request.onsuccess = () => { result = request.result; };
        transaction.oncomplete = () => resolve(result);
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(transaction.error);
    });
};

/**
 * Koalesziert den neuesten Report atomar in den Queue-Eintrag
 * (read-modify-write in einer Transaktion) — OHNE Kette. Nur für das Bündel
 * selbst, das sich vorher angestellt hat.
 */
const putLatestSofort = async (incoming: IncomingSave, nowIso: string): Promise<QueueEntry> => {
    const db = await openDb();
    return new Promise<QueueEntry>((resolve, reject) => {
        const transaction = db.transaction(STORE, 'readwrite');
        const store = transaction.objectStore(STORE);
        let entry: QueueEntry;
        const getReq = store.get([incoming.userId, incoming.projectId]) as IDBRequest<QueueEntry | undefined>;
        getReq.onsuccess = () => {
            entry = coalesceReportSave(normalizeQueueEntry(getReq.result), incoming, nowIso);
            store.put(entry);
        };
        transaction.oncomplete = () => resolve(entry);
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(transaction.error);
    });
};

/**
 * Hält die Registratur der Sicherungen (bauphaseFlush.ts) auf dem Laufenden:
 * angemeldet, solange ein Stand nur im Speicher liegt, gerade geschrieben
 * wird oder sein Schreiben GESCHEITERT ist; abgemeldet erst, wenn alles in
 * der IndexedDB steht.
 *
 * Genau dafür gibt es die Registratur — „wer Eingaben hält, die nur im
 * Speicher liegen, meldet eine Sicherung an". `sichereEingabenVorUpdate`
 * (appUpdate.ts) bestätigt erst, wenn sich alle Halter abgemeldet haben;
 * scheitert das Schreiben, bleibt die Anmeldung stehen, die Sicherung wirft
 * (`schreibeJetzt`), und das In-App-Update gilt als NICHT gesichert. Das
 * Zusperren der Bauphase ruft die Sicherung ebenfalls vor seinem Flush.
 */
let sicherungAbmelden: (() => void) | null = null;

const buendel = erzeugeSchreibbuendel({
    schreibe: save => putLatestSofort(save, new Date().toISOString()),
    setzeUhr: (fn, ms) => setTimeout(fn, ms),
    loescheUhr: uhr => { if (uhr !== undefined) clearTimeout(uhr as ReturnType<typeof setTimeout>); },
    beiBeschaeftigung: beschaeftigt => {
        if (beschaeftigt && !sicherungAbmelden) {
            sicherungAbmelden = registerBauphaseFlusher('offline-warteschlange', () => buendel.schreibeJetzt());
        } else if (!beschaeftigt && sicherungAbmelden) {
            sicherungAbmelden();
            sicherungAbmelden = null;
        }
    },
});

/**
 * Den neuesten Report GEBÜNDELT einreihen (300 ms Ruhe, spätestens 2 s).
 *
 * Löst erst auf, wenn der Stand — oder ein neuerer desselben Projekts —
 * dauerhaft in der IndexedDB steht; wirft den Fehler des Schreibvorgangs
 * (z.B. QuotaExceededError). Das ist der Vertrag, auf den sich
 * `useOfflineSync.enqueue` stützt. Der gescheiterte Stand bleibt dabei als
 * ungesichert im Bündel und wird beim nächsten Anlass erneut versucht.
 */
export const putLatestGebuendelt = (incoming: IncomingSave): Promise<void> => buendel.einreihen(incoming);

/**
 * Alles, was im Bündel wartet, SOFORT schreiben und abwarten — auch
 * gescheiterte Stände werden dabei erneut versucht.
 *
 * WIRFT, wenn danach noch ein Stand ungesichert ist: wer fragt (die
 * Registratur vor dem Update, `flushPending`), soll nicht „gesichert" hören,
 * wenn es nicht stimmt. Wer nur anstossen will (Verlassen der Seite,
 * `istBeschaeftigt`), fängt den Fehler selbst ab.
 */
export const schreibeWartendeJetzt = (): Promise<void> => buendel.schreibeJetzt();

/** Liegt ein Stand noch nur im Speicher, wird er gerade geschrieben, oder ist sein Schreiben gescheitert? */
export const lokaleSicherungOffen = (): boolean => buendel.istBeschaeftigt();

/**
 * Liegt für diesen Eintrag ein Stand, dessen Schreiben gescheitert ist? Dann
 * steht in der IndexedDB ein ÄLTERER Stand — `flushProject` lädt in diesem
 * Fall nicht hoch, sondern meldet 'storage-error'.
 *
 * Bleibt wahr, bis ein Schreiben dieses oder eines neueren Inhalts GEGLÜCKT
 * ist — auch während die Wiederholung schon angestellt ist oder läuft. Die
 * Fassung davor nahm den Stand beim Anstellen aus dem Bündel; ein Leser, der
 * vor der Wiederholung dran war, sah dann den alten Stand (Prüfung,
 * 30.09.2026).
 */
export const lokalerStandUngesichert = (userId: string, projectId: string): boolean =>
    !!buendel.ungesichert(userId, projectId);

/**
 * Der Eintrag so, wie er nach dem Schreiben des ungesicherten Stands
 * aussähe — dieselbe Regel (`coalesceReportSave`), die das Schreiben
 * anwenden wird.
 *
 * Warum Lesen hier NICHT wirft, sondern den jüngsten Stand zeigt: die Leser
 * ausserhalb des Hooks (App.tsx beim Öffnen, auf dem Rückweg und beim
 * Leerlauf-Rückzug, der Journal-Ausgangskorb) fangen einen Fehler mit
 * `.catch(() => undefined)` ab — ein Wurf hiesse dort „kein Eintrag". Das
 * Öffnen zeigte dann den Serverstand ohne Entwurf, der nächste Anschlag
 * ersetzte im Bündel den ungesicherten Stand, und dessen Inhalt wäre fort;
 * der Leerlauf-Rückzug schlösse das Projekt. Den ÄLTEREN Stand aus der
 * IndexedDB zu liefern, wäre ebenso falsch (Befund der Prüfung, 30.09.2026).
 * Der jüngste Stand ist die einzige Antwort, die keinem Leser Arbeit
 * wegnimmt. Dass er nur im Speicher liegt, meldet der Hook als
 * 'storage-error' (`lokalerStandUngesichert`, enqueue).
 */
const mitUngesichertem = (
    gelesen: QueueEntry | undefined,
    offen: UngesicherterStand | undefined,
    nowIso: string,
): QueueEntry | undefined => (offen ? coalesceReportSave(gelesen, offen.save, nowIso) : gelesen);

export const getQueueEntry = (userId: string, projectId: string): Promise<QueueEntry | undefined> =>
    buendel.nachDemWartenden(async sicht => {
        const gelesen = normalizeQueueEntry(await tx('readonly', store => store.get([userId, projectId]) as IDBRequest<QueueEntry | undefined>));
        // Nur, was VOR diesem Zugriff eingereiht war (`sicht`): ein später
        // eingetippter Stand gehört zur nächsten Generation.
        return mitUngesichertem(gelesen, sicht.ungesichert(userId, projectId), new Date().toISOString());
    });

/**
 * Den neuesten Report SOFORT sichern — ohne Bündelfenster, aber im SELBEN
 * Satz wie das Tippen (`sofort`): er ist danach der jüngste Inhalt dieses
 * Eintrags und wird im Augenblick des Aufrufs angestellt. Einen eigenen,
 * am Bündel vorbei laufenden Schreibweg gibt es bewusst nicht mehr: der
 * konnte sich zwischen einen wartenden und einen laufenden Stand schieben
 * und einen neueren Inhalt mit einem älteren überschreiben (Prüfung,
 * 30.09.2026). Löst auf, wenn dieser oder ein neuerer Inhalt desselben
 * Eintrags fest liegt; wirft den Fehler des Schreibens.
 */
export const putLatest = (incoming: IncomingSave): Promise<void> => buendel.sofort(incoming);

/**
 * Atomarer Read-Modify-Write in einer Transaktion — schützt vor Races mit
 * parallelem putLatest. Rückgabe des Mutators: null löscht den Eintrag,
 * undefined lässt ihn unverändert, sonst wird der Rückgabewert geschrieben.
 *
 * Der einzige Weg, einen Folgezustand zu schreiben — ein blindes `put(entry)`
 * gibt es bewusst nicht: es käme ohne Lesen aus, ginge damit an
 * normalizeQueueEntry vorbei und überschriebe nebenbei ein zwischenzeitliches
 * putLatest.
 *
 * Die Kette ist hier am wichtigsten: der Abschluss eines Hochladens
 * (flushProject) löscht den Eintrag, wenn `rev` noch die hochgeladene ist.
 * Wartete die letzte Eingabe dabei noch im Bündel, wäre `rev` unverändert —
 * gelöscht würde der Eintrag, und die Eingabe legte danach einen neuen an.
 * So steht sie vorher drin, `rev` ist weitergezählt, und der Eintrag bleibt
 * 'pending' liegen.
 *
 * Ein UNGESICHERTER Stand dieses Eintrags hält die Änderung bewusst NICHT
 * auf: er ist jüngere Eingabe und legt sich bei seinem nächsten Versuch über
 * `coalesceReportSave` darüber — genau so, als wäre der Anschlag nach der
 * Änderung gekommen, ein Fall, den jede Änderung hier schon immer vertragen
 * musste (Lauf eröffnen, Abschluss, Altersriegel, Verwerfen). Ein Wurf
 * dagegen liesse einen eben geglückten Upload ohne Abschluss in 'syncing'
 * stehen, und `onFlushed` (Journal-Zweitschrift, neuer Ausgangsstand) fiele
 * aus.
 */
export const mutateQueueEntry = (
    userId: string,
    projectId: string,
    mutator: (entry: QueueEntry | undefined) => QueueEntry | null | undefined,
): Promise<void> => buendel.nachDemWartenden(async () => {
    const db = await openDb();
    return new Promise<void>((resolve, reject) => {
        const transaction = db.transaction(STORE, 'readwrite');
        const store = transaction.objectStore(STORE);
        const getReq = store.get([userId, projectId]) as IDBRequest<QueueEntry | undefined>;
        getReq.onsuccess = () => {
            const result = mutator(normalizeQueueEntry(getReq.result));
            if (result === null) store.delete([userId, projectId]);
            else if (result !== undefined) store.put(result);
        };
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(transaction.error);
    });
});

/**
 * Den Eintrag ausdrücklich löschen. Erst NACH geglücktem Löschen werden auch
 * die Stände verworfen, die vor diesem Aufruf im Bündel lagen — auch ein
 * gescheiterter, dessen Wiederholung vor dem Löschen noch einmal
 * gescheitert ist; sonst tauchte der eben gelöschte Stand beim nächsten
 * Anlass wieder auf (Begründung an `Zugriffssicht.verwirfBisZurGrenze`).
 * Scheitert das Löschen, bleibt alles, wie es war. Was nach dem Aufruf
 * eingereiht wird, legt danach einen frischen Eintrag an.
 */
export const removeQueueEntry = (userId: string, projectId: string): Promise<void> =>
    buendel.nachDemWartenden(async sicht => {
        await tx('readwrite', store => { store.delete([userId, projectId]); });
        sicht.verwirfBisZurGrenze(userId, projectId);
    });

/**
 * Alle Einträge eines Benutzers — mit den ungesicherten Ständen darüber
 * gelegt (Begründung an `mitUngesichertem`): ein Projekt, dessen erstes
 * Schreiben gescheitert ist, taucht hier auf, obwohl die IndexedDB es noch
 * nicht kennt.
 */
export const listQueueEntries = (userId: string): Promise<QueueEntry[]> =>
    buendel.nachDemWartenden(async sicht => {
        const rows = await tx('readonly', store => store.index('userId').getAll(userId) as IDBRequest<QueueEntry[]>);
        const liste = (rows || []).map(entry => normalizeQueueEntry(entry));
        const nowIso = new Date().toISOString();
        for (const offen of sicht.alleUngesicherten()) {
            if (offen.save.userId !== userId) continue;
            const i = liste.findIndex(e => e.projectId === offen.save.projectId);
            const ansicht = mitUngesichertem(i >= 0 ? liste[i] : undefined, offen, nowIso)!;
            if (i >= 0) liste[i] = ansicht;
            else liste.push(ansicht);
        }
        return liste;
    });
